/**
 * HR Module - Reusable UI Components
 */
import { X, Plus, Edit2, Trash2 } from 'lucide-react';
import EmptyState from '../EmptyState';

// Modal Component - full-screen on mobile, centered dialog on desktop
export const Modal = ({ isOpen, onClose, title, children, wide }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" data-testid="hr-modal">
      <div
        className={`bg-white w-full sm:rounded-xl rounded-t-2xl ${wide ? 'sm:max-w-4xl' : 'sm:max-w-2xl'} h-[92vh] sm:max-h-[90vh] sm:h-auto overflow-y-auto`}
      >
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="font-semibold truncate pr-2">{title}</h3>
          <button
            onClick={onClose}
            className="p-2 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg hover:bg-gray-100 -mr-2"
            aria-label="Tutup"
            data-testid="modal-close-btn"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4 pb-8 sm:pb-4">{children}</div>
      </div>
    </div>
  );
};

// Tab Button Component - always shows label, min touch target
export const Tab = ({ active, onClick, icon: Icon, label, count, color = "red" }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-3 py-2 min-h-[40px] rounded-lg text-sm font-medium whitespace-nowrap shrink-0 ${
      active ? `bg-${color}-700 text-white shadow-md` : 'bg-white text-gray-600 hover:bg-gray-100 border'
    }`}
    data-testid={`hr-tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="w-4 h-4" />
    <span>{label}</span>
    {count > 0 && (
      <span className={`px-1.5 py-0.5 rounded-full text-xs ${active ? 'bg-white/20' : 'bg-red-100 text-red-700'}`}>
        {count}
      </span>
    )}
  </button>
);

// Stat Card Component
export const StatCard = ({ icon: Icon, label, value, color, alert }) => (
  <div className={`bg-white rounded-xl p-3 sm:p-4 border shadow-sm border-l-4 min-w-0 ${color} ${alert ? 'ring-2 ring-red-200' : ''}`}>
    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
      <Icon className={`w-5 h-5 shrink-0 ${color.replace('border-l-', 'text-')}`} />
      <div className="min-w-0 flex-1">
        <p className="text-gray-500 text-xs truncate">{label}</p>
        <p className="text-lg sm:text-xl font-bold">{value}</p>
      </div>
    </div>
  </div>
);

// Data Table Component
export const DataTable = ({ columns, data, onEdit, onDelete, emptyText = "Tiada rekod" }) => (
  <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col, i) => (
              <th key={i} className={`p-3 text-gray-600 font-medium ${col.align || 'text-left'}`}>
                {col.label}
              </th>
            ))}
            {(onEdit || onDelete) && <th className="p-3 text-gray-600 font-medium text-center">Aksi</th>}
          </tr>
        </thead>
        <tbody className="divide-y">
          {data.map((row, idx) => (
            <tr key={row.id || idx} className="hover:bg-gray-50">
              {columns.map((col, i) => (
                <td key={i} className={`p-3 ${col.align || ''}`}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
              {(onEdit || onDelete) && (
                <td className="p-3">
                  <div className="flex gap-1 justify-center">
                    {onEdit && (
                      <button onClick={() => onEdit(row)} className="p-1.5 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-700">
                        <Edit2 className="w-4 h-4" />
                      </button>
                    )}
                    {onDelete && (
                      <button onClick={() => onDelete(row.id)} className="p-1.5 hover:bg-red-50 rounded text-gray-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
          {data.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="p-4">
                <EmptyState title={emptyText} hint="Belum ada rekod untuk filter atau tempoh semasa." compact tone="subtle" />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Section Wrapper Component
export const Section = ({ title, onAdd, extraBtn, children }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center flex-wrap gap-2">
      <h3 className="font-semibold text-gray-800">{title}</h3>
      <div className="flex gap-2">
        {extraBtn}
        {onAdd && (
          <button onClick={onAdd} className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Tambah
          </button>
        )}
      </div>
    </div>
    {children}
  </div>
);

// Badge Components
export const Badge = ({ status }) => {
  const colors = {
    'Hadir': 'bg-emerald-100 text-emerald-700',
    'Diluluskan': 'bg-emerald-100 text-emerald-700',
    'Selesai': 'bg-emerald-100 text-emerald-700',
    'Tercapai': 'bg-emerald-100 text-emerald-700',
    'Lulus': 'bg-emerald-100 text-emerald-700',
    'Lewat': 'bg-orange-100 text-orange-700',
    'Pending': 'bg-amber-100 text-amber-700',
    'Dalam Proses': 'bg-blue-100 text-blue-700',
    'Dalam Percubaan': 'bg-blue-100 text-blue-700',
    'Dijadualkan': 'bg-blue-100 text-blue-700',
    'Tidak Hadir': 'bg-red-100 text-red-700',
    'Ditolak': 'bg-red-100 text-red-700',
    'Tidak Tercapai': 'bg-red-100 text-red-700',
    'Gagal': 'bg-red-100 text-red-700'
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

export const SkillBadge = ({ level }) => {
  const colors = {
    'Tiada': 'bg-gray-200',
    'Asas': 'bg-red-200',
    'Sederhana': 'bg-amber-200',
    'Mahir': 'bg-blue-200',
    'Pakar': 'bg-emerald-200'
  };
  return <span className={`px-2 py-1 rounded text-xs ${colors[level] || 'bg-gray-200'}`}>{level}</span>;
};

export const MoodBadge = ({ mood }) => {
  const colors = {
    'Sangat Positif': 'text-emerald-600',
    'Positif': 'text-blue-600',
    'Neutral': 'text-gray-600',
    'Negatif': 'text-orange-600',
    'Sangat Negatif': 'text-red-600'
  };
  return <span className={`text-sm ${colors[mood] || 'text-gray-600'}`}>{mood}</span>;
};
